import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Layout } from './components/layout/Layout';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { ProjectPage } from './pages/ProjectPage';
import { DiagramPage } from './pages/DiagramPage';
import { SettingsPage } from './pages/SettingsPage';
import { TrashPage } from './pages/TrashPage';
import { E2EFlowsPage } from './pages/E2EFlowsPage';
import { ImportGraphPage } from './pages/ImportGraphPage';
import { CallGraphPage } from './pages/CallGraphPage';
import { AnalysisResultsPage } from './pages/AnalysisResultsPage';
import { ArtifactsEndpointsPage } from './pages/ArtifactsEndpointsPage';
import { ArtifactsCallersPage } from './pages/ArtifactsCallersPage';
import { SchemaPage } from './pages/SchemaPage';
import { StitchReviewPage } from './pages/StitchReviewPage';
import { ClickStitchReviewPage } from './pages/ClickStitchReviewPage';
import { FrontendGraphPage } from './pages/FrontendGraphPage';
import { BackendGraphPage } from './pages/BackendGraphPage';

/**
 * #1039: redirect /projects/:id/e2e-flows → /projects/:id/flows.
 * Reads :projectId via useParams and emits an absolute Navigate so the
 * legacy URL nudges users toward the canonical one (instead of dual-
 * canonical forever).
 */
function RedirectToFlows() {
  const { projectId } = useParams<{ projectId: string }>();
  if (!projectId) return <Navigate to="/dashboard" replace />;
  return <Navigate to={`/projects/${projectId}/flows`} replace />;
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        {/* #1038: bare /projects had no Route → blank page. Redirect to dashboard. */}
        <Route path="projects" element={<Navigate to="/dashboard" replace />} />
        <Route path="projects/:projectId" element={<ProjectPage />} />
        <Route path="projects/:projectId/flows" element={<E2EFlowsPage />} />
        {/* #1039: redirect legacy /e2e-flows → canonical /flows.
            The previous <Navigate to="../flows"> resolved to /flows
            (no route → blank). Use an absolute path built from the
            current :projectId via useParams so the redirect always
            lands on the right canonical URL. */}
        <Route path="projects/:projectId/e2e-flows" element={<RedirectToFlows />} />
        <Route path="projects/:projectId/import-graph" element={<ImportGraphPage />} />
        <Route path="projects/:projectId/call-graph" element={<CallGraphPage />} />
        <Route path="projects/:projectId/schema" element={<SchemaPage />} />
        <Route path="projects/:projectId/frontend-graph" element={<FrontendGraphPage />} />
        <Route path="projects/:projectId/backend-graph" element={<BackendGraphPage />} />
        {/* Stitch review UI (issue #812, epic #802) */}
        <Route path="projects/:projectId/stitch-review" element={<StitchReviewPage />} />
        {/* Click stitch review UI (issue #963 track 3) */}
        <Route path="projects/:projectId/click-stitch-review" element={<ClickStitchReviewPage />} />
        <Route path="projects/:projectId/analysis-results" element={<AnalysisResultsPage />} />
        {/* Flat routes for API artifacts */}
        <Route path="projects/:projectId/endpoints" element={<ArtifactsEndpointsPage />} />
        <Route path="projects/:projectId/callers" element={<ArtifactsCallersPage />} />
        {/* Redirect legacy /artifacts/* paths */}
        <Route path="projects/:projectId/artifacts" element={<Navigate to=".." replace />} />
        <Route path="projects/:projectId/artifacts/endpoints" element={<Navigate to="../endpoints" replace />} />
        <Route path="projects/:projectId/artifacts/callers" element={<Navigate to="../callers" replace />} />
        <Route path="diagrams/:diagramId" element={<DiagramPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="trash" element={<TrashPage />} />
      </Route>
    </Routes>
  );
}

export default App;
